import type { Book, BookSearchResult, Task } from "#/types";

import { useRequireAuth } from "#/feature/auth/use-require-auth";
import {
  Book as BookIcon,
  CheckCircle2,
  Clock,
  Copy,
  Download,
  Globe,
  Loader2,
  Puzzle,
  RefreshCw,
  Search,
  X,
  XCircle,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { cx } from "styled-system/css";

import * as styles from "./jobs.css";
import { useJobs } from "./use-jobs";
import { Button } from "#/components/button/button";
import { Card } from "#/components/card/card";
import { searchBooks } from "#/client";
import { useSignal } from "#/use-signal";
import { bookIdFilterSignal, sourceFilterSignal } from "./job-filters";
import { setUrlSearchParamsSetter } from "#/stores/url-filter-store";
import { token } from "styled-system/tokens";

export function Jobs() {
  const allowed = useRequireAuth({ admin: true });
  const {
    jobs,
    library,
    loadingState,
    loadingMore,
    hasMore,
    cancelJob,
    loadMore,
    fetchData,
    isRefreshing,
  } = useJobs();
  const [, setSearchParams] = useSearchParams();

  // Register React Router's setSearchParams with url-filter-store
  useEffect(() => {
    setUrlSearchParamsSetter(setSearchParams);
    return () => setUrlSearchParamsSetter(null);
  }, [setSearchParams]);

  // Read filter values from signals
  const bookIds = useSignal(bookIdFilterSignal);
  const source = useSignal(sourceFilterSignal);

  // Derived: book filter objects with titles
  const bookFilters = useMemo(() => {
    return bookIds
      .map((id: number) => library.find((b: Book) => b.id === id))
      .filter((b: Book | undefined): b is Book => b !== undefined)
      .map((b: Book) => ({ id: b.id, title: b.title, source_id: b.source_id }));
  }, [bookIds, library]);
  const [showBookDropdown, setShowBookDropdown] = useState(false);
  const [showSourceDropdown, setShowSourceDropdown] = useState(false);
  const [bookSearchQuery, setBookSearchQuery] = useState("");
  const [bookSearchResults, setBookSearchResults] = useState<BookSearchResult[]>([]);
  const [searchBookLoading, setSearchBookLoading] = useState(false);
  const bookInputRef = useRef<HTMLInputElement>(null);
  const sourceRef = useRef<HTMLDivElement>(null);

  // Derive unique sources from currently loaded jobs
  const uniqueSources = useMemo(() => {
    const sources = new Set<string>();
    jobs.forEach((job) => {
      const book = library.find((b) => b.id === job.book_id);
      if (book) {
        sources.add(book.source_id);
      }
    });
    return Array.from(sources).sort();
  }, [jobs, library]);

  // Debounced book search
  const debouncedSearch = useMemo(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (query: string) => {
      clearTimeout(timeoutId);
      if (query.length < 2) {
        setBookSearchResults([]);
        return;
      }
      setSearchBookLoading(true);
      timeoutId = setTimeout(async () => {
        try {
          const results = await searchBooks(query, 20);
          setBookSearchResults(results);
        } catch (error) {
          console.error("Failed to search books:", error);
        } finally {
          setSearchBookLoading(false);
        }
      }, 300);
    };
  }, []);

  useEffect(() => {
    debouncedSearch(bookSearchQuery);
  }, [bookSearchQuery, debouncedSearch]);

  // Handle book selection (add to filters)
  const handleBookSelect = (book: BookSearchResult) => {
    const current = bookIdFilterSignal();
    if (!current.includes(book.id)) {
      bookIdFilterSignal([...current, book.id]);
    }
    setBookSearchQuery("");
    setBookSearchResults([]);
    setShowBookDropdown(false);
  };

  // Handle source selection
  const handleSourceSelect = (selectedSource: string) => {
    sourceFilterSignal(selectedSource === source ? null : selectedSource);
    setShowSourceDropdown(false);
  };

  // Remove book filter
  const removeBookFilter = (bookId: number) => {
    const current = bookIdFilterSignal();
    bookIdFilterSignal(current.filter((id: number) => id !== bookId));
  };

  // Clear source filter
  const clearSourceFilter = () => {
    sourceFilterSignal(null);
  };

  // Clear all filters
  const clearAllFilters = () => {
    bookIdFilterSignal([]);
    sourceFilterSignal(null);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (bookInputRef.current && !bookInputRef.current.contains(event.target as Node)) {
        setShowBookDropdown(false);
      }
      if (sourceRef.current && !sourceRef.current.contains(event.target as Node)) {
        setShowSourceDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!allowed) return null;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Download jobs</h1>
        <button
          onClick={() => void fetchData()}
          disabled={isRefreshing}
          className={styles.refreshBtn}
          aria-label="Refresh"
        >
          <RefreshCw size={20} className={cx(isRefreshing && styles.spin)} />
        </button>
      </div>

      <JobsFilters
        bookSearchQuery={bookSearchQuery}
        setBookSearchQuery={setBookSearchQuery}
        showBookDropdown={showBookDropdown}
        setShowBookDropdown={setShowBookDropdown}
        bookSearchResults={bookSearchResults}
        searchBookLoading={searchBookLoading}
        handleBookSelect={handleBookSelect}
        bookFilters={bookFilters}
        removeBookFilter={removeBookFilter}
        source={source}
        uniqueSources={uniqueSources}
        handleSourceSelect={handleSourceSelect}
        showSourceDropdown={showSourceDropdown}
        setShowSourceDropdown={setShowSourceDropdown}
        clearAllFilters={clearAllFilters}
        bookInputRef={bookInputRef}
        sourceRef={sourceRef}
      />

      {loadingState && jobs.length === 0 ? (
        <div className={styles.loading}>Loading tasks...</div>
      ) : (
        <JobsList
          jobs={jobs}
          library={library}
          onCancel={cancelJob}
          loadMore={loadMore}
          loadingMore={loadingMore}
          hasMore={hasMore}
          emptyContent={<div className={styles.empty}>No background tasks found.</div>}
        />
      )}
    </div>
  );
}

interface JobsFiltersProps {
  bookSearchQuery: string;
  setBookSearchQuery: (value: string) => void;
  showBookDropdown: boolean;
  setShowBookDropdown: (value: boolean) => void;
  bookSearchResults: BookSearchResult[];
  searchBookLoading: boolean;
  handleBookSelect: (book: BookSearchResult) => void;
  bookFilters: BookSearchResult[];
  removeBookFilter: (bookId: number) => void;
  source: string | null;
  uniqueSources: string[];
  handleSourceSelect: (source: string) => void;
  showSourceDropdown: boolean;
  setShowSourceDropdown: (value: boolean) => void;
  clearAllFilters: () => void;
  bookInputRef: React.RefObject<HTMLInputElement | null>;
  sourceRef: React.RefObject<HTMLDivElement | null>;
}

const JobsFilters = ({
  bookSearchQuery,
  setBookSearchQuery,
  showBookDropdown,
  setShowBookDropdown,
  bookSearchResults,
  searchBookLoading,
  handleBookSelect,
  bookFilters,
  removeBookFilter,
  source,
  uniqueSources,
  handleSourceSelect,
  showSourceDropdown,
  setShowSourceDropdown,
  clearAllFilters,
  bookInputRef,
  sourceRef,
}: JobsFiltersProps) => {
  const hasActiveFilters = bookFilters.length > 0 || source !== null;
  const spacingSm = token("spacing.sm");

  return (
    <div className={styles.filterContainer}>
      <div className={styles.filterRow}>
        {/* Book Filter - Autocomplete */}
        <div className={styles.filterInputWrapper} ref={bookInputRef}>
          <div className={styles.filterInputContainer}>
            <Search size={20} className={styles.filterInputIcon} />
            <input
              type="text"
              value={bookSearchQuery}
              onChange={(e) => {
                setBookSearchQuery(e.target.value);
                setShowBookDropdown(true);
              }}
              onFocus={() => setShowBookDropdown(true)}
              placeholder="Enter novel title"
              className={styles.filterInput}
            />
          </div>
          {showBookDropdown && bookSearchQuery.length >= 2 && (
            <div className={styles.filterDropdown}>
              {searchBookLoading ? (
                <div style={{ padding: "0.5rem", textAlign: "center", opacity: 0.5 }}>
                  <Loader2 size={16} className={styles.spin} />
                </div>
              ) : bookSearchResults.length > 0 ? (
                bookSearchResults.map((book) => (
                  <button
                    key={book.id}
                    onClick={() => handleBookSelect(book)}
                    className={styles.filterDropdownItem}
                  >
                    {book.title}
                  </button>
                ))
              ) : (
                <div style={{ padding: spacingSm, opacity: 0.5 }}>No results</div>
              )}
            </div>
          )}
        </div>

        {/* Source Filter - Icon button */}
        <div className={styles.sourceFilterWrapper} ref={sourceRef}>
          <button
            onClick={() => setShowSourceDropdown(!showSourceDropdown)}
            className={styles.sourceFilterButton}
            style={
              source
                ? {
                    backgroundColor: `color-mix(in srgb, ${token("colors.primary")}, transparent 90%)`,
                    color: token("colors.primary"),
                    borderColor: `color-mix(in srgb, ${token("colors.primary")}, transparent 80%)`,
                  }
                : undefined
            }
            aria-label="Filter by source"
          >
            <Globe size={20} />
          </button>
          {showSourceDropdown && (
            <div className={styles.sourceDropdown}>
              {uniqueSources.length > 0 ? (
                uniqueSources.map((source) => (
                  <button
                    key={source}
                    onClick={() => handleSourceSelect(source)}
                    className={styles.filterDropdownItem}
                  >
                    {source}
                  </button>
                ))
              ) : (
                <div style={{ padding: spacingSm, opacity: 0.5 }}>No sources</div>
              )}
            </div>
          )}
        </div>

        {/* Clear All Button */}
        {hasActiveFilters && (
          <button onClick={clearAllFilters} className={styles.clearFiltersBtn}>
            Clear All
          </button>
        )}
      </div>

      {/* Active Filter Chips */}
      {hasActiveFilters && (
        <div className={styles.filterChipsContainer}>
          {bookFilters.map((book) => (
            <div key={book.id} className={styles.filterChip}>
              <span>Book: {book.title}</span>
              <button
                onClick={() => removeBookFilter(book.id)}
                className={styles.filterChipButton}
                aria-label="Clear book filter"
              >
                <X size={12} />
              </button>
            </div>
          ))}
          {source && (
            <div className={styles.filterChip}>
              <span>Source: {source}</span>
              <button
                onClick={() => handleSourceSelect(source)}
                className={styles.filterChipButton}
                aria-label="Clear source filter"
              >
                <X size={12} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface JobsListProps {
  jobs: Task[];
  library: Book[];
  onCancel: (id: number) => void;
  loadMore: () => void;
  loadingMore: boolean;
  hasMore: boolean;
  emptyContent: React.ReactNode;
}

const JobsList = ({
  jobs,
  library,
  onCancel,
  loadMore,
  loadingMore,
  hasMore,
  emptyContent,
}: JobsListProps) => {
  const loaderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target && target.isIntersecting && !loadingMore && hasMore) {
          loadMore();
        }
      },
      {
        root: null,
        rootMargin: "20px",
        threshold: 0.1,
      },
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [loadingMore, hasMore, loadMore]);

  if (jobs.length === 0) {
    return <>{emptyContent}</>;
  }

  return (
    <div className={styles.listContainer}>
      <div className={styles.mobileCardGrid}>
        {jobs.map((job) => {
          const book = library.find((libBook) => libBook.id === job.book_id);
          return <JobCard key={job.id} job={job} book={book} onCancel={() => onCancel(job.id)} />;
        })}
      </div>

      {/* Shared Loader */}
      <div ref={loaderRef} className={styles.listFooter}>
        {loadingMore
          ? "Loading more..."
          : hasMore
            ? "Load more"
            : `End of list (${jobs.length} jobs)`}
      </div>
    </div>
  );
};

interface JobItemProps {
  job: Task;
  book: Book | undefined;
  onCancel: () => void;
}

const JobCard = ({ job, book, onCancel }: JobItemProps) => {
  const statusStyle =
    job.state === "Completed"
      ? styles.statusTextCompleted
      : job.state === "Cancelled"
        ? styles.statusTextCancelled
        : job.state === "Error"
          ? styles.statusTextError
          : job.state === "Running"
            ? styles.statusTextRunning
            : job.state === "Queued"
              ? styles.statusTextQueued
              : styles.statusTextDefault;

  return (
    <Card className={styles.mobileListItem}>
      <Card.Body className={styles.mobileCardInner}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "start",
            gap: "0.5rem",
          }}
        >
          <div className={styles.jobCardLeft} style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                marginBottom: "0.25rem",
              }}
            >
              <Link
                to={`/book/${job.book_id}`}
                style={{
                  fontWeight: "700",
                  fontSize: "14px",
                  color: "inherit",
                  textDecoration: "none",
                  wordBreak: "break-word",
                }}
              >
                {book ? book.title : `Book #${job.book_id}`}
              </Link>
              {job.is_extension && (
                <div className={styles.extensionBadge}>
                  <Puzzle size={12} />
                  <span className={styles.extensionText}>Ext</span>
                </div>
              )}
            </div>

            <div style={{ paddingLeft: 0 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  fontSize: "10px",
                  opacity: 0.6,
                  marginBottom: "0.25rem",
                }}
              >
                {(job.state === "Completed" ||
                  job.state === "Cancelled" ||
                  job.state === "Error") && (
                  <span className={styles.statusIconInline}>
                    <StatusIcon state={job.state} />
                  </span>
                )}
                <span className={cx(styles.statusText, statusStyle)}>{job.state}</span>
                <span>•</span>
                <span>{job.downloaded_chapters} ch</span>
                <span>•</span>
                <span>{job.username || "System"}</span>
              </div>
              {job.last_downloaded_chapter_title && (
                <div
                  style={{
                    fontSize: "10px",
                    opacity: 0.4,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {job.last_downloaded_chapter_title}
                </div>
              )}
            </div>
          </div>

          <div
            className={styles.jobCardRight}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "end",
              gap: "0.5rem",
              flexShrink: 0,
            }}
          >
            <span className={styles.timestamp}>
              {new Date(`${job.created_at}Z`).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
            <div style={{ display: "flex", gap: "0.25rem", alignItems: "center" }}>
              {job.is_extension && job.state !== "Cancelled" && job.state !== "Completed" && (
                <button
                  onClick={() => {
                    if (navigator.clipboard) {
                      void navigator.clipboard.writeText(job.id.toString());
                    } else {
                      console.warn("Clipboard API not available");
                      // Fallback for non-secure contexts if needed
                      const textArea = document.createElement("textarea");
                      textArea.value = job.id.toString();
                      document.body.appendChild(textArea);
                      textArea.select();
                      try {
                        document.execCommand("copy");
                      } catch (err) {
                        console.error("Fallback: Oops, unable to copy", err);
                      }
                      document.body.removeChild(textArea);
                    }
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.25rem",
                    padding: "0.25rem 0.5rem",
                    fontSize: "9px",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    color: `color-mix(in srgb, ${token("colors.textBase")}, transparent 60%)`,
                    backgroundColor: "transparent",
                    border: "1px solid color-mix(in srgb, currentColor, transparent 80%)",
                    borderRadius: "4px",
                    cursor: "pointer",
                    transition: "all 150ms",
                  }}
                  title="Copy job ID"
                >
                  <Copy size={10} />
                  <span>ID</span>
                </button>
              )}
              {(job.state === "Running" || job.state === "Queued") && (
                <Button
                  onClick={onCancel}
                  variant="ghost"
                  size="small"
                  className={styles.cancelBtn}
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

const StatusIcon = ({ state }: { state: string }) => {
  const styleProps = styles.iconSmall;

  switch (state) {
    case "Completed": {
      return (
        <CheckCircle2 size={20} className={styleProps} style={{ color: token("colors.success") }} />
      );
    }
    case "Cancelled":
    case "Error": {
      return <XCircle size={20} className={styleProps} style={{ color: token("colors.error") }} />;
    }
    case "Running": {
      return (
        <Loader2
          size={20}
          className={cx(styleProps, styles.spin)}
          style={{ color: token("colors.primary") }}
        />
      );
    }
    case "Queued": {
      return <Clock size={20} className={styleProps} style={{ opacity: 0.5 }} />;
    }
    default: {
      return <Download size={20} className={styleProps} style={{ opacity: 0.5 }} />;
    }
  }
};

interface ProgressDisplayProps {
  job: Task;
}

const ProgressDisplay = ({ job }: ProgressDisplayProps) => {
  const isRunning = job.state === "Running" || job.state === "Queued";

  return (
    <div style={{ marginBottom: "0.5rem" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "0.25rem",
        }}
      >
        <span
          style={{
            fontSize: "9px",
            fontWeight: "900",
            color: token("colors.primary"),
            textTransform: "uppercase",
          }}
        >
          {isRunning ? "Scraping..." : ""}
        </span>
        <span style={{ fontSize: "9px", fontWeight: "700", opacity: 0.4 }}>
          {job.downloaded_chapters} chapters
        </span>
      </div>
    </div>
  );
};

const JobChaptersInfo = ({ job }: { job: Task }) => (
  <div className={styles.jobChaptersInfo}>
    {job.start_chapter && (
      <div className={styles.chapterInfoRow}>
        <span className={styles.chapterInfoLabel}>From:</span>
        {job.start_chapter}
      </div>
    )}
    {job.last_downloaded_chapter_title &&
      job.last_downloaded_chapter_title !== job.start_chapter && (
        <div className={styles.chapterInfoRow}>
          <span className={styles.chapterInfoLabel}>
            {job.state === "Running" ? "Last:" : "To:"}
          </span>
          {job.last_downloaded_chapter_title}
        </div>
      )}
  </div>
);
